<template>
  <MediaGallery
    ref="galleryRef"
    title="Photos"
    :items="photos"
    :job-id="jobId"
    :owner-id="ownerId"
    :owner-param-key="isCustomerScope ? 'customerId' : undefined"
    media-type="photo"
    item-type-label="Photo"
    card-class="photo-gallery"
    :has-max-size-text="true"
    :selectable="true"
    :readonly="readonly"
    :gallery-options="galleryOptions"
    :get-thumbnail-url="getThumbnailUrl"
    @delete-success="(id) => emit('delete-success', id)"
  >
    <template #header-extra>
      <span class="font-sm mr-2"><b>Max size per photo:</b> 20MB</span>
    </template>
    <template #upload-button>
      <SeraButton icon="fa-solid fa-images" class="md:w-[210px]">
        <label class="mb-0 cursor-pointer">
          Upload Photo(s)
          <input
            ref="fileInputRef"
            type="file"
            :accept="PHOTO_ACCEPT"
            multiple
            class="hidden"
            @change="handleUpload"
          />
        </label>
      </SeraButton>
    </template>
    <template #progress-modal>
      <MediaUploadProgressModal
        v-if="uploading"
        media-type="photo"
        :file-count="selectedFiles.length"
        :total-file-size="totalFileSize"
        :progress="progress"
        @hide-modal="handleCancel"
      />
    </template>
    <template
      #viewer-modal="{
        item,
        close,
        openMode,
        canGoPrevious,
        canGoNext,
        goPrevious,
        goNext,
        saveCaption,
        deleteCaption
      }"
    >
      <PhotoViewerModal
        v-if="item"
        :photo="item"
        :open-mode="openMode"
        :readonly="readonly"
        :can-go-previous="canGoPrevious"
        :can-go-next="canGoNext"
        :go-previous="goPrevious"
        :go-next="goNext"
        @hide-modal="close"
        @save-caption="saveCaption"
        @delete-caption="deleteCaption"
      />
    </template>
  </MediaGallery>
</template>

<script setup lang="ts">
import escape from "lodash/escape";
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";
import {
  partitionPhotos,
  PHOTO_ACCEPT,
  unsupportedPhotoMessage
} from "@/lib/photoUploads";

import SeraButton from "@/components/Button.vue";
import MediaGallery from "@/components/MediaGallery.vue";
import MediaUpload from "@/components/MediaUpload";
import MediaUploadProgressModal from "@/components/MediaUploadProgressModal.vue";
import CONSTANTS from "@/Constants.js";
import { FailedUpload, MediaFile } from "@/typings/media-upload";
import { Photo } from "@/typings/photo";
import PhotoViewerModal from "./PhotoViewerModal.vue";

const { MEGABYTE, PHOTO_MAX_SIZE, PHOTO_MAX_COUNT } = CONSTANTS;

interface UploadLink {
  href: string;
  method?: string;
}

const props = withDefaults(
  defineProps<{
    photos: Photo[];
    jobId: number | null;
    uploadLink?: UploadLink;
    readonly?: boolean;
    // 'customer' points the delete/visibility/caption actions at the customer
    // endpoints and makes only customer-scoped items editable in the gallery.
    scope?: "job" | "customer";
    customerId?: number | string | null;
  }>(),
  {
    photos: () => [],
    jobId: null,
    uploadLink: undefined,
    readonly: false,
    scope: "job",
    customerId: null
  }
);

const emit = defineEmits<{
  "delete-success": [photoId: number];
  "upload-success": [];
}>();

// Template refs
const fileInputRef = ref<HTMLInputElement | null>(null);
const galleryRef = ref<InstanceType<typeof MediaGallery> | null>(null);

// Upload state
const uploading = ref(false);
const selectedFiles = ref<MediaFile[]>([]);
const progress = ref(0);

let uploader: MediaUpload | null = null;

// Gallery options for useGalleryActions. Customer scope routes to the S4
// customer endpoints and omits the job-keyed realtime events (customer photos
// carry no job_id, so those EventBus updates never apply).
const galleryOptions = computed(() =>
  props.scope === "customer"
    ? {
        deleteAction: "deleteCustomerPhoto",
        visibilityAction: "updateCustomerPhotoVisibility",
        visibilityParamKey: "photoId",
        captionAction: "updateCustomerPhotoCaption"
      }
    : {
        deleteAction: "deleteJobPhoto",
        visibilityAction: "updateJobPhotoVisibility",
        visibilityParamKey: "photoId",
        captionAction: "updateJobPhotoCaption",
        thumbnailEventName: "photo-thumbnail-ready"
      }
);

const isCustomerScope = computed(() => props.scope === "customer");
const ownerId = computed(() =>
  isCustomerScope.value && props.customerId != null
    ? Number(props.customerId)
    : null
);

// Computed
const totalFileSize = computed(() => {
  return selectedFiles.value.reduce((total, file) => {
    return total + file.file.size;
  }, 0);
});

function getThumbnailUrl(photo: Photo): string {
  if (!photo || !photo?.file) return "";
  const { file, created_at } = photo;
  let src = file.thumb?.url || file.url;
  // Use full image URL if recently uploaded (thumb may not be ready)
  if (moment().diff(moment(created_at), "minutes") < 2) {
    src = file.url;
  }
  return src;
}

// Methods
function clearInput() {
  if (fileInputRef.value) fileInputRef.value.value = "";
}

function handleUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files) return;

  const { storable, rejected } = partitionPhotos(Array.from(target.files));

  if (rejected.length) window.notify.error(unsupportedPhotoMessage(rejected));

  // Counted after the partition, because the cap is enforced per request and
  // only the storable files reach it. Cleared on both refusals, or re-picking
  // the same corrected selection fires no change event and the picker looks
  // dead.
  if (storable.length > PHOTO_MAX_COUNT) {
    window.notify.error(
      `You can only upload a max of ${PHOTO_MAX_COUNT} photos at one time. Try splitting photos into multiple uploads.`
    );
    clearInput();
    return;
  }

  if (!storable.length) {
    clearInput();
    return;
  }

  // Each file used to be read through a FileReader first and the data URL
  // thrown away, so a batch was base64-encoded in memory for nothing — and a
  // single read that never fired left the count short and the upload never
  // started. MediaUpload only ever wanted the File and its name.
  // A change handler cannot return a promise, and the refetch inside can fail
  // on its own; this only stops an unhandled rejection.
  uploadPhotos(
    storable.map((file) => ({
      file,
      fileName: file.name
    }))
  ).catch(() => {});
}

async function uploadPhotos(files: MediaFile[]) {
  if (!props.uploadLink) return;
  uploading.value = true;
  selectedFiles.value = files;
  uploader = new MediaUpload({
    mediaType: "photo",
    link: props.uploadLink,
    files
  });
  uploader.onProgress = ({ progress: prog }) => {
    progress.value = prog * 100;
  };

  // Cleared in a finally, as the form modal already does: a reserve call that
  // rejects used to leave the progress modal up and the input dirty, so the same
  // files could not be re-picked.
  //
  // State only, deliberately. resetUploader also emits upload-success, which
  // makes the parent refetch — handleUploadComplete has already done that on the
  // success path, and an upload that stored nothing has nothing to announce.
  try {
    await uploader.uploadAll();
    handleUploadComplete();
  } finally {
    clearUploadState();
    clearInput();
  }
}

function formatFileSize(bytes: number): string {
  return `${(bytes / MEGABYTE).toFixed(2)}MB`;
}

function getFailureReason(upload: FailedUpload): string {
  if (upload.reason === "too_large") {
    const maxSizeMB = Math.round(PHOTO_MAX_SIZE / MEGABYTE);
    return `exceeds maximum size of ${maxSizeMB}MB`;
  }
  return "failed to upload";
}

function handleUploadComplete() {
  if (!uploader) return;
  try {
    const { failures, success } = uploader;
    let message = `${success} photo(s) uploaded successfully!`;
    // The toast is assigned through innerHTML and the alert is not, so the file
    // names are escaped for one and left alone for the other. One shared string
    // showed &#39; to anyone with an apostrophe in a file name.
    let plain = message;
    if (failures) {
      const heading = `${failures} out of ${
        success + failures
      } photo(s) failed to upload.<br/><br/>`;
      message = heading;
      plain = heading;

      uploader.failedUploads.forEach((upload, i) => {
        const fileSize = formatFileSize(upload.file.size);
        const reason = getFailureReason(upload);
        message += `${i + 1}) ${escape(
          upload.fileName
        )} (${fileSize}) - ${reason}<br/>`;
        plain += `${i + 1}) ${upload.fileName} (${fileSize}) - ${reason}<br/>`;
      });

      window.notify.error(message);
    } else {
      window.notify.success(message);
    }
    if (document.hidden) {
      alert(plain.replace(/<br\/>/gm, "\n"));
    }
  } catch {
    // no-op
  }
  resetUploader();
}

function handleCancel() {
  if (uploader) {
    uploader.onProgress = null;
    uploader.cancel();
    window.notify.error(`Cancelled uploading photo(s)`);
  }
  resetUploader();
}

function clearUploadState() {
  uploading.value = false;
  selectedFiles.value = [];
  progress.value = 0;
  uploader = null;
}

function resetUploader() {
  clearUploadState();
  emit("upload-success");
  setTimeout(() => {
    const modal = document.querySelector(".modal") as HTMLElement | null;
    if (modal) {
      modal.style.display = "none";
    }
  }, 750);
}

// Lifecycle
onMounted(() => {
  EventBus.$on("job-photo-upload-complete", handleUploadComplete);
  window.onbeforeunload = (e: BeforeUnloadEvent) => {
    if (uploading.value) {
      e.preventDefault();
      return "Your pending photos will not be uploaded. Are you sure?";
    }
  };
});

onBeforeUnmount(() => {
  EventBus.$off("job-photo-upload-complete", handleUploadComplete);
  window.onbeforeunload = null;
});

// Expose for testing
defineExpose({
  uploading,
  selectedFiles,
  progress,
  totalFileSize,
  get deleteId() {
    return galleryRef.value?.deleteId;
  },
  get deleteConfirm() {
    return galleryRef.value?.deleteConfirm;
  },
  get viewerItem() {
    return galleryRef.value?.viewerItem;
  },
  handleDelete: (id: number) => galleryRef.value?.handleDelete(id),
  handleVisibilityToggle: (item: Photo) =>
    galleryRef.value?.handleVisibilityToggle(item),
  closeDeleteConfirm: () => galleryRef.value?.closeDeleteConfirm(),
  removePhoto: async () => {
    // Handled by MediaGallery
  }
});
</script>
